/**
 * Pure spatial-column detection over a preview result shape — feeds the
 * add-tile shape autopilot (map suggestion) and the map config section.
 *
 * BUNDLE ISOLATION: only the EAGER spatial surfaces are imported here —
 * `categorizeSpatialColumn` (dataType check) and, through it, the header
 * sniffer `spatialSniff.ts` — never wkx / decodeSpatialBatch / maplibre
 * (those stay in the lazy chunk, see Docs/Spatial.md + the
 * `scripts/assert-spatial-lazy-chunks.mjs` gate).
 *
 * Two arms, mirroring `spatialResultColumns.ts` without the MetaInfo
 * subscription (this module must stay pure and store-free):
 *  1. dataType arm — the engine type is a native spatial type
 *     (`geometry(Point,4326)`, `SDO_GEOMETRY`, …);
 *  2. value arm — sample values header-sniff as the engine's spatial wire
 *     format (EWKB hex, SpatiaLite/GPKG blobs, CLR, SDO marker). Same
 *     promotion rule as `sniffSpatialBlobColumns`: at least one non-null
 *     value sniffs spatial AND no non-null value fails — a mixed column of
 *     geometry + garbage is never promoted.
 */
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { QueryColumn } from '../../../../types/queryExecution';
import { categorizeSpatialColumn } from '../../common/utils/spatialColumnCategorization';
import { sniffSpatialValue } from '../../spatial/decode/spatialSniff';

const MAX_SNIFF_ROWS = 20;

function sniffsSpatial(
  engine: DatabaseEngineId,
  key: string,
  dataType: string,
  rows: Array<Record<string, unknown>>,
): boolean {
  let sawSpatial = false;
  const limit = Math.min(rows.length, MAX_SNIFF_ROWS);
  for (let i = 0; i < limit; i += 1) {
    const value = rows[i]?.[key];
    if (value === null || value === undefined) continue;
    if (sniffSpatialValue(engine, value, dataType)) sawSpatial = true;
    else return false; // one non-null non-spatial value disqualifies the column
  }
  return sawSpatial;
}

/**
 * Column names (result-row keys, `id ?? name`) that hold geometry. Cheap and
 * sync: dataType lookup + a capped header sniff over ≤20 sample rows. Without
 * an engine nothing can be sniffed or type-matched — returns [].
 */
export function detectSpatialColumnNames(
  columns: QueryColumn[],
  rows: Array<Record<string, unknown>>,
  engine: DatabaseEngineId | undefined,
): string[] {
  if (!engine || engine === 'kusto') return [];
  const names: string[] = [];
  for (const column of columns) {
    const key = column.id ?? column.name;
    const dataType = column.dataType ?? '';
    const byType =
      categorizeSpatialColumn(
        {
          engine,
          schema: column.sourceSchema ?? '',
          table: column.sourceTable ?? '',
          name: column.sourceColumn ?? column.name,
          dataType,
        },
        null,
      ) === 'native-spatial';
    if (byType || sniffsSpatial(engine, key, dataType, rows)) names.push(key);
  }
  return names;
}
